// DeployContentWithLinks.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

/**
 * DeployContentWithLinks (tenant-aware)
 *
 * Props:
 *  - mode: "deploy" | "add-edit"  (default "deploy")
 *  - platformUrl?: string (required for crawler/membership)
 *  - backEndUrl: string (required)
 *  - tenantId?: string  // forwarded as x-tenant-id to backend (recommended)
 *  - resourceId?: string (default "default")
 *  - encodedState?: string (required to deploy in "deploy" mode")
 *  - initialTitle?: string (default "Content With Links")
 *  - deployedCourseKey?: string             // for migration source course
 *  - deployedCourseKeyType?: "courseUuid" | "courseId"  // optional hint
 *  - deployedResourceId?: string            // for migration source resource
 */
const DeployContentWithLinks = ({
                                    mode = "deploy",
                                    platformUrl = "",
                                    backEndUrl,
                                    tenantId,
                                    resourceId = "default",
                                    encodedState = "",
                                    initialTitle = "Content With Links",
                                    deployedCourseKey,
                                    deployedCourseKeyType,
                                    deployedResourceId,
                                }) => {
    /** -------- identity & auth -------- */
    const qs = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
    const token = qs.get("token") || "";
    const courseUuid = qs.get("courseUuid") || "";
    // membership: use ?uuid=... (fallback to userUuid if present)
    const userUuid = qs.get("uuid") || qs.get("userUuid") || "";

    // Fallback to localStorage if uuid isn't provided
    const courseIdFromLS =
        (typeof window !== "undefined" && window.localStorage.getItem("courseId")) || "";

    // For REST paths: use uuid: only when we actually have a UUID
    const courseAddress = useMemo(() => {
        if (courseUuid) return `uuid:${courseUuid}`;
        if (courseIdFromLS) return encodeURIComponent(courseIdFromLS);
        return "";
    }, [courseUuid, courseIdFromLS]);

    // For backend payloads (current course key)
    const courseKeyForApi = useMemo(() => {
        if (courseUuid) return { courseUuid };
        if (courseIdFromLS) return { courseId: courseIdFromLS };
        return null;
    }, [courseUuid, courseIdFromLS]);

    // Build a persistent migration lock key (course + resourceId)
    const migrationLockKey = useMemo(() => {
        const courseKeyStr = courseUuid || courseIdFromLS || "unknownCourse";
        return `cwl:migrated:${courseKeyStr}:${String(resourceId || "default")}`;
    }, [courseUuid, courseIdFromLS, resourceId]);

    // Helper: detect UUID-ish (very lenient)
    const isUuidLike = (s) =>
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(s || ""));

    // Source course key (for migration)
    const sourceCourseKeyForApi = useMemo(() => {
        const key = deployedCourseKey || "";
        if (!key) return null;
        const type = deployedCourseKeyType || (isUuidLike(key) ? "courseUuid" : "courseId");
        return type === "courseUuid" ? { courseUuid: key } : { courseId: key };
    }, [deployedCourseKey, deployedCourseKeyType]);

    const authHeaders = useMemo(
        () => ({ headers: { Authorization: `Bearer ${token}` } }),
        [token]
    );

    /** -------- tenant header helpers -------- */
    const tenantHeaderName = "x-tenant-id"; // keep in sync with backend TENANT_HEADER
    const tenantHeaders = useMemo(() => {
        const h = {};
        if (tenantId) h[tenantHeaderName] = tenantId;
        return h;
    }, [tenantId]);

    // Convenience to merge headers per request
    const withHeaders = (extra = {}) => ({
        headers: {
            ...tenantHeaders,
            ...(extra.headers || {}),
        },
        ...(extra || {}),
    });

    /** -------------------- state -------------------- */
    const [isWorking, setIsWorking] = useState(false);
    const [notice, setNotice] = useState("");
    const [error, setError] = useState("");

    const [html, setHtml] = useState("<p><strong>Welcome!</strong> Start composing your content here…</p>");
    const [links, setLinks] = useState([]);
    const [filter, setFilter] = useState("");
    const [deployTitle, setDeployTitle] = useState(initialTitle);
    const textAreaRef = useRef(null);

    // add-edit mode state
    const [isInstructor, setIsInstructor] = useState(false);
    const [checkedMembership, setCheckedMembership] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    // migration guards
    const migrationRanRef = useRef(false);

    /** ----------------- helpers (crawler) ----------------- */
    const getPaged = async (url) => {
        let results = [];
        let next = url;
        while (next) {
            const { data } = await axios.get(next, authHeaders);
            results = results.concat(data?.results || []);
            next = data?.paging?.nextPage || null;
        }
        return results;
    };

    const isContainerHandler = (handlerId = "") => {
        const id = String(handlerId || "").toLowerCase();
        return id.includes("folder") || id.includes("learningmodule") || id.includes("lesson") || id.includes("module");
    };

    const makeAbsolute = (base, href) => {
        try {
            return new URL(href, base).toString();
        } catch {
            const a = String(base || "").replace(/\/+$/, "");
            const b = String(href || "").replace(/^\/+/, "");
            return `${a}/${b}`;
        }
    };

    const fetchRootContents = async () => {
        if (!courseAddress) throw new Error("Missing course identifier (courseUuid or courseId).");
        const base = `${platformUrl}/learn/api/public/v1/courses/${courseAddress}/contents?parentId=ROOT&limit=200`;
        return await getPaged(base);
    };

    const fetchChildren = async (contentId) => {
        if (!courseAddress) throw new Error("Missing course identifier (courseUuid or courseId).");
        const base = `${platformUrl}/learn/api/public/v1/courses/${courseAddress}/contents/${encodeURIComponent(
            contentId
        )}/children?limit=200`;
        return await getPaged(base);
    };

    const getPreferredItemUrl = (item) => {
        if (Array.isArray(item?.links)) {
            for (const l of item.links) {
                if (l?.href) {
                    return l.href.startsWith("http") ? l.href : makeAbsolute(platformUrl, l.href);
                }
            }
        }
        return "";
    };

    /**
     * Crawl the tree and carry parentTitle/parentUrl forward for document items.
     * For course-link discussions, keep their own href.
     */
    const crawlContents = async () => {
        const all = [];
        const queue = [{ id: "ROOT", parentId: null, _root: true, title: "(root)", preferredUrl: "" }];

        const rootChildren = await fetchRootContents();
        for (const ch of rootChildren) {
            const preferredUrl = getPreferredItemUrl(ch);
            all.push({ ...ch, parentId: "ROOT", parentTitle: "(root)", parentUrl: "", preferredUrl });
            queue.push({
                id: ch.id,
                parentId: "ROOT",
                title: ch.title || ch.name || "Untitled",
                handlerId: ch?.contentHandler?.id || "",
                hasChildren: !!ch?.hasChildren,
                preferredUrl,
            });
        }

        while (queue.length > 0) {
            const node = queue.shift();
            if (!node || node._root) continue;

            const cur = all.find((i) => i.id === node.id);
            const nodeHandlerId = node.handlerId || cur?.contentHandler?.id || "";
            const nodeHasChildren = node.hasChildren ?? cur?.hasChildren ?? false;

            if (!(nodeHasChildren || isContainerHandler(nodeHandlerId))) continue;

            try {
                const kids = await fetchChildren(node.id);
                for (const k of kids) {
                    const kidPreferredUrl = getPreferredItemUrl(k);
                    all.push({
                        ...k,
                        parentId: node.id,
                        parentTitle: node.title,
                        parentUrl: node.preferredUrl || getPreferredItemUrl(cur) || "",
                        preferredUrl: kidPreferredUrl,
                    });
                    const kidHandler = k?.contentHandler?.id || "";
                    queue.push({
                        id: k.id,
                        parentId: node.id,
                        title: k.title || k.name || "Untitled",
                        handlerId: kidHandler,
                        hasChildren: !!k?.hasChildren,
                        preferredUrl: kidPreferredUrl,
                    });
                }
            } catch (e) {
                console.warn("Children fetch failed for", node.id, e?.message);
            }
        }

        return all;
    };

    const isLikelyUrl = (u) => /^https?:\/\/|^www\./i.test(String(u || ""));
    const uniqueByUrl = (arr) => {
        const seen = new Set();
        const out = [];
        for (const item of arr) {
            const u = (item.url || "").trim();
            if (!u || seen.has(u)) continue;
            seen.add(u);
            out.push(item);
        }
        return out;
    };

    const extractLinksFromItems = (items) => {
        const all = [];
        for (const it of items) {
            const contentTitle = it?.title || it?.name || "Untitled";
            const parentTitle = it?.parentTitle || contentTitle;
            const parentUrl = it?.parentUrl || "";
            const handlerId = (it?.contentHandler?.id || "").toLowerCase();
            const isDocument = handlerId.includes("document");
            const isCourseLink = handlerId.includes("courselink");

            const linkLabel = isDocument ? parentTitle : contentTitle;

            if (Array.isArray(it?.links)) {
                for (const l of it.links) {
                    const href = l?.href;
                    if (!href) continue;
                    const abs = href.startsWith("http") ? href : makeAbsolute(platformUrl, href);
                    const finalUrl = isDocument && parentUrl ? parentUrl : abs;

                    all.push({
                        url: finalUrl,
                        title: isCourseLink ? contentTitle : linkLabel,
                        contentTitle: isCourseLink ? contentTitle : linkLabel,
                        sourceTitle: contentTitle,
                        origin: isCourseLink ? "discussion-courselink" : "platform-link",
                    });
                }
            }

            const blocks = [
                it?.body,
                it?.instructions,
                it?.documentBody,
                it?.ultraBody,
                it?.description,
                it?.content?.html,
                it?.contentHandler?.instructions,
            ];
            for (const b of blocks) {
                if (typeof b === "string" && b.trim()) {
                    const hrefRegex = /<a\b[^>]*\bhref\s*=\s*["']([^"']+)["'][^>]*>(.*?)<\/a>/gim;
                    let m;
                    while ((m = hrefRegex.exec(b))) {
                        const href = (m[1] || "").trim();
                        if (!isLikelyUrl(href)) continue;
                        const abs = href.startsWith("http") ? href : makeAbsolute(platformUrl, href);
                        const finalUrl = isDocument && parentUrl ? parentUrl : abs;

                        all.push({
                            url: finalUrl,
                            title: isCourseLink ? contentTitle : linkLabel,
                            contentTitle: isCourseLink ? contentTitle : linkLabel,
                            sourceTitle: contentTitle,
                            origin: isCourseLink ? "discussion-anchor" : "html-anchor",
                        });
                    }
                }
            }
        }
        return uniqueByUrl(all);
    };

    /** ---------------- backend API (tenant-aware) ---------------- */
    const apiGet = async () => {
        if (!backEndUrl) throw new Error("Missing backEndUrl.");
        if (!courseKeyForApi) throw new Error("Missing courseUuid in URL or courseId in localStorage.");

        const params = new URLSearchParams({ resourceId: String(resourceId || "default") });
        if (courseKeyForApi.courseUuid) params.set("courseUuid", courseKeyForApi.courseUuid);
        if (courseKeyForApi.courseId) params.set("courseId", courseKeyForApi.courseId);

        const url = `${backEndUrl.replace(/\/+$/, "")}/content-with-links?${params.toString()}`;
        const { data } = await axios.get(url, withHeaders());
        return data?.item || null;
    };

    // Fetch content-with-links for an explicit course key (used for migration)
    const apiGetForCourseKey = async ({ courseUuid: cu, courseId: cid }, resId) => {
        if (!backEndUrl) throw new Error("Missing backEndUrl.");
        const params = new URLSearchParams({ resourceId: String(resId || "default") });
        if (cu) params.set("courseUuid", cu);
        if (cid) params.set("courseId", cid);
        const url = `${backEndUrl.replace(/\/+$/, "")}/content-with-links?${params.toString()}`;
        const { data } = await axios.get(url, withHeaders());
        return data?.item || null;
    };

    const apiPut = async (payload) => {
        if (!backEndUrl) throw new Error("Missing backEndUrl.");
        const url = `${backEndUrl.replace(/\/+$/, "")}/content-with-links`;
        const body = {
            ...(courseKeyForApi || {}),
            resourceId: String(resourceId || "default"),
            ...payload,
        };
        const { data } = await axios.put(
            url,
            body,
            withHeaders({ headers: { "Content-Type": "application/json", ...tenantHeaders } })
        );
        return data?.item || null;
    };

    /** --------------- computed --------------- */
    const filteredLinks = useMemo(() => {
        const q = (filter || "").toLowerCase().trim();
        if (!q) return links;
        return links.filter(
            (l) =>
                (l.contentTitle || "").toLowerCase().includes(q) ||
                (l.title || "").toLowerCase().includes(q) ||
                (l.url || "").toLowerCase().includes(q) ||
                (l.sourceTitle || "").toLowerCase().includes(q)
        );
    }, [links, filter]);

    /** --------------- effects --------------- */
    // Load saved content; optionally crawl (deploy mode)
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setError("");
            setIsWorking(true);
            try {
                // Optional guard: if your backend requires tenantId, nudge the user early.
                if (!tenantId) {
                    // Don’t hard-fail; some envs might not require tenant.
                    console.warn("TenantId is not provided; backend may reject calls if tenant is required.");
                }

                const existing = await apiGet().catch(() => null);
                if (!cancelled && existing) {
                    if (typeof existing.Html === "string") setHtml(existing.Html);
                    if (Array.isArray(existing.Links)) setLinks(existing.Links);
                    const alreadyMigrated =
                        !!existing?.Meta?.migratedFrom || !!existing?.meta?.migratedFrom;
                    if (alreadyMigrated) {
                        try {
                            window.localStorage.setItem(migrationLockKey, "1");
                        } catch {}
                        migrationRanRef.current = true;
                    }
                    setNotice("Loaded saved content.");
                    setTimeout(() => setNotice(""), 2000);
                }

                if (mode === "deploy" && platformUrl && token && courseAddress && !cancelled) {
                    try {
                        const items = await crawlContents();
                        if (!cancelled) setLinks(extractLinksFromItems(items));
                    } catch (e) {
                        console.warn("Crawler failed:", e?.message || e);
                    }
                }
            } finally {
                if (!cancelled) setIsWorking(false);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [platformUrl, backEndUrl, resourceId, courseAddress, mode, migrationLockKey, tenantId]);

    // Membership check (add-edit mode)
    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (mode !== "add-edit") return;
            setCheckedMembership(false);
            try {
                if (!(platformUrl && token && courseAddress && userUuid)) {
                    setCheckedMembership(true);
                    return;
                }
                const userAddress = `uuid:${encodeURIComponent(userUuid)}`;
                const url = `${platformUrl}/learn/api/public/v1/courses/${courseAddress}/users/${userAddress}`;
                const { data } = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
                const role = String(data?.courseRoleId || "").toLowerCase();
                if (!cancelled) setIsInstructor(role.includes("instructor"));
            } catch (e) {
                console.warn("Course user lookup failed:", e?.message || e);
                if (!cancelled) setIsInstructor(false);
            } finally {
                if (!cancelled) setCheckedMembership(true);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, platformUrl, token, courseAddress, userUuid]);

    // Auto-crawl when entering edit mode (so links are available to insert)
    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!(mode === "add-edit" && isEditing)) return;
            if (!(platformUrl && token && courseAddress)) return;
            try {
                setIsWorking(true);
                const items = await crawlContents();
                if (!cancelled) setLinks(extractLinksFromItems(items));
            } catch (e) {
                console.warn("Crawler during edit failed:", e?.message || e);
            } finally {
                if (!cancelled) setIsWorking(false);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditing, mode, platformUrl, token, courseAddress]);

    /** ---------------- Migration ---------------- */
        // Rewrites anchor hrefs using a title->url map from the current course.
    const rewriteHtmlWithTitleMap = (sourceHtml, titleToUrl) => {
            try {
                const container = document.createElement("div");
                container.innerHTML = sourceHtml || "";
                const anchors = container.querySelectorAll("a[href]");
                anchors.forEach((a) => {
                    const text = (a.textContent || "").trim().toLowerCase();
                    if (!text) return;
                    const matchUrl = titleToUrl.get(text);
                    if (matchUrl) {
                        a.setAttribute("href", matchUrl);
                        a.setAttribute("target", "_blank");
                        a.setAttribute("rel", "noopener noreferrer");
                    }
                });
                return container.innerHTML;
            } catch {
                return sourceHtml || "";
            }
        };

    // Attempt migration exactly once; never again after we mark the local lock
    useEffect(() => {
        const shouldMigrate =
            Boolean(deployedResourceId) &&
            String(deployedResourceId) !== String(resourceId) &&
            !migrationRanRef.current;

        if (!shouldMigrate) return;
        if (!sourceCourseKeyForApi) return; // need a source course key

        // Hard stop if the lock says we've already migrated for this course+resource
        try {
            if (window.localStorage.getItem(migrationLockKey) === "1") {
                migrationRanRef.current = true;
                return;
            }
        } catch {}

        let cancelled = false;

        (async () => {
            try {
                setIsWorking(true);
                setNotice("");
                setError("");

                // 1) Load source item (from deployed course)
                const sourceItem = await apiGetForCourseKey(sourceCourseKeyForApi, deployedResourceId);
                const sourceHtml = sourceItem?.Html || "";
                const sourceLinks = Array.isArray(sourceItem?.Links) ? sourceItem.Links : [];

                if (!sourceHtml && sourceLinks.length === 0) {
                    setNotice("Nothing to migrate from the source item.");
                    migrationRanRef.current = true;
                    try { window.localStorage.setItem(migrationLockKey, "1"); } catch {}
                    return;
                }

                // 2) Crawl current course to build a title->url map
                if (!(platformUrl && token && courseAddress)) {
                    setError("Cannot migrate: missing platformUrl/token/current course.");
                    return;
                }

                const items = await crawlContents(); // current course tree
                const titleToUrl = new Map();
                items.forEach((it) => {
                    const handlerId = (it?.contentHandler?.id || "").toLowerCase();
                    const isDocument = handlerId.includes("document");
                    const label = (isDocument ? (it?.parentTitle || it?.title || it?.name) : (it?.title || it?.name) || "Untitled")
                        .trim()
                        .toLowerCase();
                    const url = it?.preferredUrl || getPreferredItemUrl(it) || "";
                    if (label && url && !titleToUrl.has(label)) titleToUrl.set(label, url);
                });

                // 3) Rewrite HTML
                const migratedHtml = rewriteHtmlWithTitleMap(sourceHtml, titleToUrl);

                // 4) Rewrite Links array (match by contentTitle/title -> URL)
                const migratedLinks = sourceLinks.map((l) => {
                    const label = (l?.contentTitle || l?.title || "").trim().toLowerCase();
                    const mapped = titleToUrl.get(label);
                    return mapped ? { ...l, url: mapped } : l;
                });

                // 5) Save to current course/resource
                const saved = await apiPut({
                    html: migratedHtml || sourceHtml,
                    links: migratedLinks,
                    meta: {
                        resourceId: String(resourceId || "default"),
                        migratedFrom: {
                            courseKey: deployedCourseKey || null,
                            courseKeyType: sourceCourseKeyForApi?.courseUuid
                                ? "courseUuid"
                                : sourceCourseKeyForApi?.courseId
                                    ? "courseId"
                                    : null,
                            resourceId: deployedResourceId,
                        },
                    },
                });

                if (!cancelled) {
                    if (typeof saved?.Html === "string") setHtml(saved.Html);
                    if (Array.isArray(saved?.Links)) setLinks(saved.Links);
                    setNotice(`Migrated content from ${deployedResourceId} to ${resourceId}.`);
                    migrationRanRef.current = true;
                    try { window.localStorage.setItem(migrationLockKey, "1"); } catch {}
                }
            } catch (e) {
                if (!cancelled) setError(e?.response?.data?.message || e?.message || "Migration failed.");
            } finally {
                if (!cancelled) setIsWorking(false);
            }
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deployedCourseKey, deployedResourceId, resourceId, platformUrl, token, courseAddress, migrationLockKey]);

    /** --------------- actions --------------- */
    const refreshLinks = async () => {
        if (!platformUrl) return setError("Missing platformUrl.");
        if (!token) return setError("Missing access token (?token=...).");
        if (!courseAddress) return setError("Missing courseUuid in URL or courseId in localStorage.");

        setIsWorking(true);
        setError("");
        setNotice("");
        try {
            const items = await crawlContents();
            const derived = extractLinksFromItems(items);
            setLinks(derived);
            setNotice(`Found ${derived.length} links in course content.`);
            setTimeout(() => setNotice(""), 2500);
        } catch (e) {
            console.error(e);
            setError(e?.response?.data?.message || e?.message || "Failed to load course contents.");
        } finally {
            setIsWorking(false);
        }
    };

    const escapeHtml = (s) =>
        String(s || "")
            .replaceAll("&", "&amp;")
            .replaceAll("<", "&lt;")
            .replaceAll(">", "&gt;")
            .replaceAll('"', "&quot;")
            .replaceAll("'", "&#39;");

    const insertHtmlAtCursor = (htmlToInsert) => {
        const ta = textAreaRef.current;
        const current = html || "";
        if (!ta) {
            setHtml(current + htmlToInsert);
            return;
        }
        const start = ta.selectionStart ?? current.length;
        const end = ta.selectionEnd ?? current.length;
        const before = current.slice(0, start);
        const after = current.slice(end);
        const next = `${before}${htmlToInsert}${after}`;
        setHtml(next);
        requestAnimationFrame(() => {
            const pos = start + htmlToInsert.length;
            ta.focus();
            ta.setSelectionRange(pos, pos);
        });
    };

    const placeAtCursor = (openTag, closeTag = "") => {
        const ta = textAreaRef.current;
        const current = html || "";
        if (!ta) {
            setHtml(current + openTag + closeTag);
            return;
        }
        const start = ta.selectionStart ?? current.length;
        const end = ta.selectionEnd ?? current.length;
        const before = current.slice(0, start);
        const selected = current.slice(start, end);
        const after = current.slice(end);
        const next = `${before}${openTag}${selected}${closeTag}${after}`;
        setHtml(next);
        requestAnimationFrame(() => {
            const pos = start + openTag.length + selected.length + closeTag.length;
            ta.focus();
            ta.setSelectionRange(pos, pos);
        });
    };

    const insertLink = (url, contentTitle) => {
        const safeText = escapeHtml(contentTitle || url);
        const anchor = `<a href="${url}" target="_blank" rel="noopener noreferrer">${safeText}</a>`;
        insertHtmlAtCursor(anchor);
    };

    const insertAllFiltered = () => {
        if (filteredLinks.length === 0) return;
        const ul =
            "<ul>" +
            filteredLinks
                .map((l) => {
                    const linkText = l.contentTitle || l.title || l.url;
                    return `<li><a href="${l.url}" target="_blank" rel="noopener noreferrer">${escapeHtml(
                        linkText
                    )}</a></li>`;
                })
                .join("") +
            "</ul>";
        placeAtCursor(ul);
    };

    const saveToBackend = async () => {
        setError("");
        setNotice("");
        try {
            const saved = await apiPut({
                html,
                links,
                meta: { resourceId: String(resourceId || "default") },
            });
            if (saved?.Html && saved.Html !== html) setHtml(saved.Html);
            if (Array.isArray(saved?.Links)) setLinks(saved.Links);
            setNotice("Saved.");
            setTimeout(() => setNotice(""), 2200);
            if (mode === "add-edit") setIsEditing(false);
        } catch (e) {
            console.error("Save failed", e);
            setError(e?.response?.data?.message || e?.message || "Save failed.");
        }
    };

    /** --------------- Deep Link Deploy (NO grade column) --------------- */
    const handleDeploy = async () => {
        setNotice("");
        setError("");

        if (!backEndUrl) return setError("Missing backEndUrl.");
        if (!encodedState) return setError("Missing encodedState for Deep Linking.");

        const title = (deployTitle || "Content With Links").trim();
        const slugify = (s) => (s || "").toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-");

        const slugBase = slugify(title || "content-with-links");
        const slug = `${slugBase}-${slugify(String(resourceId || "default"))}`;

        const previewSrc = `${backEndUrl.replace(/\/+$/, "")}/lti/preview/${slug}`;
        const launchUrl = `${backEndUrl.replace(/\/+$/, "")}/lti/launch/${slug}`;

        const deployedCourseKeyOut = courseUuid || courseIdFromLS || "";

        const selectedItems = [
            {
                id: `lti-embed-1`,
                type: "ltiResourceLink",
                label: title,
                title,
                url: launchUrl,
                custom: {
                    assignmentName: title,
                    route: "DeployContentWithLinks",
                    deployedResourceId: String(resourceId || "default"),
                    deployedCourseKey: deployedCourseKeyOut,
                    deployedCourseKeyType: courseUuid ? "courseUuid" : (courseIdFromLS ? "courseId" : "unknown"),
                    mode: "add-edit",
                },
                embed: {
                    html: `<iframe width="800" height="450" src="${previewSrc}" frameborder="0" allowfullscreen></iframe>`,
                },
                iframe: {
                    width: 800,
                    height: 450,
                    src: previewSrc,
                },
            },
        ];

        try {
            setIsWorking(true);
            const res = await axios.post(
                `${backEndUrl.replace(/\/+$/, "")}/lti/deep-link?state=${encodeURIComponent(encodedState)}`,
                { selectedItems },
                // deep-link may not require tenant, but pass it harmlessly if present
                withHeaders({ headers: { "Content-Type": "application/json", ...tenantHeaders } })
            );

            const jwt = res?.data?.jwt;
            const deepLinkReturnUrl = res?.data?.deepLinkReturnUrl;
            if (!jwt || !deepLinkReturnUrl) throw new Error("Backend response missing jwt or deepLinkReturnUrl.");

            const form = document.createElement("form");
            form.method = "POST";
            form.action = deepLinkReturnUrl;
            form.enctype = "application/x-www-form-urlencoded";

            const input = document.createElement("input");
            input.type = "hidden";
            input.name = "JWT";
            input.value = jwt;

            form.appendChild(input);
            document.body.appendChild(form);
            form.submit();
        } catch (e) {
            console.error("Deployment failed:", e);
            setError(e?.response?.data?.message || e?.message || "Deployment failed.");
        } finally {
            setIsWorking(false);
        }
    };

    /** -------------------- render -------------------- */
    const hasContent = !!(html && html.trim().length);

    const RenderAlerts = () => (
        <>
            {notice && (
                <div className="bb-alert mb-3 p-3 rounded border border-green-300 bg-green-50 text-green-800">{notice}</div>
            )}
            {error && (
                <div className="bb-alert mb-3 p-3 rounded border border-red-300 bg-red-50 text-red-800">{error}</div>
            )}
            {!tenantId && (
                <div className="bb-alert mb-3 p-3 rounded border border-amber-300 bg-amber-50 text-amber-800">
                    Tenant is not set. If your backend enforces tenant isolation, set the <code>tenantId</code> prop.
                </div>
            )}
        </>
    );

    // ----------- ADD-EDIT MODE -----------
    if (mode === "add-edit") {
        return (
            <div className="bb-container max-w-5xl mx-auto p-4">
                <RenderAlerts />

                <div className="flex items-center gap-3 mb-3">
                    {checkedMembership && isInstructor && !isEditing && (
                        <button
                            className="bb-button px-3 py-2 rounded border hover:opacity-90"
                            onClick={() => setIsEditing(true)}
                            title="Edit HTML"
                        >
                            Edit
                        </button>
                    )}
                    {isEditing && (
                        <>
                            <button
                                className="bb-button px-3 py-2 rounded border bg-blue-600 text-white hover:opacity-90"
                                onClick={saveToBackend}
                                disabled={isWorking}
                                title="Save changes"
                            >
                                {isWorking ? "Saving…" : "Save"}
                            </button>
                            <button
                                className="bb-button px-3 py-2 rounded border hover:opacity-90"
                                onClick={() => setIsEditing(false)}
                                disabled={isWorking}
                                title="Cancel editing"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={refreshLinks}
                                disabled={isWorking}
                                className={`bb-button px-3 py-2 rounded border ${
                                    isWorking ? "opacity-60 cursor-not-allowed" : "hover:opacity-90"
                                }`}
                                title="Refresh links from course (crawler)"
                            >
                                {isWorking ? "Refreshing…" : "Refresh Links"}
                            </button>
                        </>
                    )}
                </div>

                {!isEditing ? (
                    <div className="bb-card rounded border p-4">
                        <div className="mb-2"><strong>Display</strong></div>
                        <div
                            className="prose max-w-none"
                            dangerouslySetInnerHTML={{ __html: hasContent ? html : "<p><em>(Empty)</em></p>" }}
                        />
                    </div>
                ) : (
                    <>
                        {/* Editor + Preview */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Editor */}
                            <div className="bb-card rounded border p-4">
                                <div className="mb-2"><strong>HTML Editor</strong></div>
                                <textarea
                                    ref={textAreaRef}
                                    className="bb-input w-full font-mono text-sm"
                                    rows={12}
                                    value={html}
                                    onChange={(e) => setHtml(e.target.value)}
                                    placeholder="<p>Start typing your HTML…</p>"
                                />
                                <div className="text-xs text-gray-500 mt-2">
                                    Tip: Click “Insert” on a link to add <code>&lt;a&gt;</code> using the item title.
                                </div>
                            </div>

                            {/* Live Preview */}
                            <div className="bb-card rounded border p-4">
                                <div className="mb-2"><strong>Live Preview</strong></div>
                                <div
                                    className="prose max-w-none"
                                    dangerouslySetInnerHTML={{ __html: hasContent ? html : "<p><em>(Empty)</em></p>" }}
                                />
                            </div>
                        </div>

                        {/* Links list (available while editing) */}
                        <div className="bb-card rounded border p-4 mt-4">
                            <div className="flex flex-wrap items-center gap-3 mb-2">
                                <div><strong>Discovered Links</strong></div>
                                <input
                                    className="bb-input w-64"
                                    placeholder="Filter links…"
                                    value={filter}
                                    onChange={(e) => setFilter(e.target.value)}
                                />
                                <div className="text-sm text-gray-600 ml-auto">
                                    Showing {filteredLinks.length} of {links.length}
                                </div>
                                <button
                                    onClick={insertAllFiltered}
                                    disabled={filteredLinks.length === 0}
                                    className={`bb-button px-3 py-2 rounded border ${
                                        filteredLinks.length === 0 ? "opacity-60 cursor-not-allowed" : "hover:opacity-90"
                                    }`}
                                    title="Insert list of all filtered links at cursor (uses titles per rule)"
                                >
                                    Insert All Filtered Links
                                </button>
                            </div>

                            {filteredLinks.length === 0 ? (
                                <div className="text-gray-600">No links found. Try Refresh or adjust the filter.</div>
                            ) : (
                                <div className="rounded border overflow-hidden">
                                    {filteredLinks.map((l, idx) => {
                                        const mainTitle = l.contentTitle || l.title || l.url;
                                        return (
                                            <div
                                                key={`${l.url}-${idx}`}
                                                className="flex items-start gap-2 p-2 border-b last:border-b-0"
                                            >
                                                <div
                                                    className="text-xs px-2 py-1 rounded-full border bg-gray-100 text-gray-700"
                                                    style={{ minWidth: 28, textAlign: "center" }}
                                                    aria-hidden="true"
                                                >
                                                    {idx + 1}
                                                </div>

                                                <div className="flex-1 min-w-[200px]">
                                                    <div className="leading-5 truncate"><strong>{mainTitle}</strong></div>
                                                    <a
                                                        href={l.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="block text-blue-700 truncate"
                                                        title={l.url}
                                                    >
                                                        {l.url}
                                                    </a>
                                                </div>

                                                <div className="flex gap-2 shrink-0">
                                                    <button
                                                        className="bb-button px-2 py-1 rounded border"
                                                        onClick={() => insertLink(l.url, l.contentTitle)}
                                                        title="Insert link using title (documents use parent title)"
                                                    >
                                                        Insert
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        );
    }

    // ----------- DEPLOY MODE (default) -----------
    return (
        <div className="bb-container max-w-6xl mx-auto p-4">
            <RenderAlerts />

            {/* Top actions */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
                <button
                    onClick={refreshLinks}
                    disabled={isWorking}
                    className={`bb-button px-3 py-2 rounded border ${
                        isWorking ? "opacity-60 cursor-not-allowed" : "hover:opacity-90"
                    }`}
                    title="Refresh links from course (crawler)"
                >
                    {isWorking ? "Refreshing…" : "Refresh Links"}
                </button>

                <button
                    onClick={insertAllFiltered}
                    disabled={filteredLinks.length === 0}
                    className={`bb-button px-3 py-2 rounded border ${
                        filteredLinks.length === 0 ? "opacity-60 cursor-not-allowed" : "hover:opacity-90"
                    }`}
                    title="Insert list of all filtered links at cursor (uses titles per rule)"
                >
                    Insert All Filtered Links
                </button>

                <button
                    onClick={saveToBackend}
                    className="bb-button px-3 py-2 rounded bg-blue-600 text-white hover:opacity-90"
                    title="Save so it appears for students"
                >
                    Save
                </button>

                {/* Deploy controls */}
                <div className="ml-auto flex items-center gap-2">
                    <input
                        className="bb-input"
                        style={{ minWidth: 260 }}
                        value={deployTitle}
                        onChange={(e) => setDeployTitle(e.target.value)}
                        placeholder="Deployment Title"
                        title="Title used for the LTI link"
                    />
                    <button
                        onClick={handleDeploy}
                        disabled={!encodedState || isWorking}
                        className={`bb-button px-3 py-2 rounded ${
                            !encodedState || isWorking ? "opacity-60 cursor-not-allowed" : "hover:opacity-90"
                        } bg-emerald-600 text-white`}
                        title={encodedState ? "Deploy deep link to Blackboard" : "encodedState missing from launch"}
                    >
                        {isWorking ? "Deploying…" : "Deploy to Blackboard"}
                    </button>
                </div>
            </div>

            {/* Editor + Preview */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Editor */}
                <div className="bb-card rounded border p-4">
                    <div className="mb-2"><strong>HTML Editor</strong></div>
                    <textarea
                        ref={textAreaRef}
                        className="bb-input w-full font-mono text-sm"
                        rows={10}
                        value={html}
                        onChange={(e) => setHtml(e.target.value)}
                        placeholder="<p>Start typing your HTML…</p>"
                    />
                    <div className="text-xs text-gray-500 mt-2">
                        Tip: Click “Insert” on a link to add <code>&lt;a&gt;</code> using the item title.
                    </div>
                </div>

                {/* Live Preview */}
                <div className="bb-card rounded border p-4">
                    <div className="mb-2"><strong>Live Preview</strong></div>
                    <div
                        className="prose max-w-none"
                        dangerouslySetInnerHTML={{ __html: hasContent ? html : "<p><em>(Empty)</em></p>" }}
                    />
                </div>
            </div>

            {/* Links list */}
            <div className="bb-card rounded border p-4 mt-4">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                    <div><strong>Discovered Links</strong></div>
                    <input
                        className="bb-input w-64"
                        placeholder="Filter links…"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                    />
                    <div className="text-sm text-gray-600 ml-auto">
                        Showing {filteredLinks.length} of {links.length}
                    </div>
                </div>

                {filteredLinks.length === 0 ? (
                    <div className="text-gray-600">No links found. Try Refresh or adjust the filter.</div>
                ) : (
                    <div className="rounded border overflow-hidden">
                        {filteredLinks.map((l, idx) => {
                            const mainTitle = l.contentTitle || l.title || l.url;
                            return (
                                <div
                                    key={`${l.url}-${idx}`}
                                    className="flex items-start gap-2 p-2 border-b last:border-b-0"
                                >
                                    <div
                                        className="text-xs px-2 py-1 rounded-full border bg-gray-100 text-gray-700"
                                        style={{ minWidth: 28, textAlign: "center" }}
                                        aria-hidden="true"
                                    >
                                        {idx + 1}
                                    </div>

                                    <div className="flex-1 min-w-[200px]">
                                        <div className="leading-5 truncate"><strong>{mainTitle}</strong></div>
                                        <a
                                            href={l.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block text-blue-700 truncate"
                                            title={l.url}
                                        >
                                            {l.url}
                                        </a>
                                    </div>

                                    <div className="flex gap-2 shrink-0">
                                        <button
                                            className="bb-button px-2 py-1 rounded border"
                                            onClick={() => insertLink(l.url, l.contentTitle)}
                                            title="Insert link using title (documents use parent title)"
                                        >
                                            Insert
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DeployContentWithLinks;
